// Detects an AI reply that ends by asking for go-ahead ("Do you want me to
// proceed?", "Shall I proceed with this?", "Proceed?") so chat surfaces can
// offer one-click Yes / No replies. Only the closing line counts, so a reply
// that merely mentions "proceed" mid-way doesn't get the buttons.
const PROCEED_QUESTION_RE = /\bproceed\b[^?]*\?\s*$/i;

export const asksToProceed = (message: string | null | undefined): boolean => {
  const plain = (message ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[*_`~>#]+/g, '')
    .trim();
  const lastLine = plain.split('\n').map((l) => l.trim()).filter(Boolean).pop() ?? '';
  return PROCEED_QUESTION_RE.test(lastLine);
};

// Sent verbatim as the user's reply, so the agent gets an unambiguous answer.
export const PROCEED_YES_REPLY = 'Yes, proceed.';
export const PROCEED_NO_REPLY = 'No, do not proceed.';

export type QuickReplyOption = { label: string; reply: string };

// ── Explicit button marker from the AI ───────────────────────────────────────
// $$SHOWMODAL_<question text><Buttons>[YES|NO]$$
//   e.g. $$SHOWMODAL_Do you want me to send this email?<Buttons>[YES|NO]$$
//        $$SHOWMODAL_How should I file this?<Buttons>[Renewal|Endorsement|Other]$$
// The marker is removed from the displayed text; its question renders above one
// button per label, and clicking a button sends that label back as the reply.
const QUICK_PROMPT_RE = /\$\$SHOWMODAL_([\s\S]*?)<\s*Buttons\s*>\s*\[([^\]]*)\]\s*\$\$/gi;
const QUICK_PROMPT_OPEN = '$$SHOWMODAL_';

export type AiQuickPrompt = { text: string; options: QuickReplyOption[] };

// While a reply streams, the marker arrives in pieces — hide an unterminated
// one (or a trailing fragment of its opener, e.g. "$$SHOW") so raw marker text
// never flashes on screen before it is complete. Streaming only: a finished
// message ending in "$$" is another marker's closer (charts etc.), not ours.
const stripPartialQuickPrompt = (content: string): string => {
  const upper = content.toUpperCase();
  const openAt = upper.lastIndexOf(QUICK_PROMPT_OPEN);
  if (openAt !== -1) return content.slice(0, openAt).trimEnd();
  for (let len = QUICK_PROMPT_OPEN.length - 1; len >= 3; len--) {
    if (upper.endsWith(QUICK_PROMPT_OPEN.slice(0, len))) {
      return content.slice(0, content.length - len).trimEnd();
    }
  }
  return content;
};

/**
 * Pulls $$SHOWMODAL_…$$ markers out of an AI message. Returns the text to
 * display (markers removed) and the last well-formed prompt, if any. Content
 * without a marker comes back untouched.
 */
export const parseQuickPrompt = (
  content: string | null | undefined,
  streaming = false,
): { cleaned: string; prompt: AiQuickPrompt | null } => {
  const raw = content ?? '';
  if (!raw.includes('$$')) return { cleaned: raw, prompt: null };
  let prompt: AiQuickPrompt | null = null;
  let cleaned = raw.replace(QUICK_PROMPT_RE, (_m, text: string, labels: string) => {
    const options = labels
      .split('|')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((label) => ({ label, reply: label }));
    if (options.length) prompt = { text: text.trim(), options };
    return '';
  });
  if (streaming) cleaned = stripPartialQuickPrompt(cleaned);
  return { cleaned: cleaned === raw ? raw : cleaned.trim(), prompt };
};

/** Plain-text previews (cards, snippets): a marker becomes just its question text. */
export const quickPromptMarkersToText = (content: string): string =>
  content.replace(QUICK_PROMPT_RE, (_m, text: string) => text.trim());

// ── Backend flag ─────────────────────────────────────────────────────────────
// The terminal answer event (responseStreaming === false) of /claude-managed/chat
// and /momentum-kanban-stream carries the backend's own decision on a go-ahead
// question (utils/proceed_prompt.py in IGIGMomentumMyAMP):
//   { show_proceed_buttons: boolean,
//     proceed_prompt: { text: string, options: { label, reply }[] } | null }
// `text` is "" for a plain closing "Do you want me to proceed?" (the question is
// already the last line of the answer) and the options are the Yes / No pair
// below. A $$SHOWMODAL_…$$ marker yields its question and labels instead.
export const DEFAULT_PROCEED_OPTIONS: QuickReplyOption[] = [
  { label: 'Yes', reply: PROCEED_YES_REPLY },
  { label: 'No', reply: PROCEED_NO_REPLY },
];

export type ApiProceedPromptEvent = {
  show_proceed_buttons?: boolean | null;
  proceed_prompt?: {
    text?: string | null;
    options?: { label?: string | null; reply?: string | null }[] | null;
  } | null;
};

/**
 * The backend's decision on a terminal answer event:
 *   - a prompt when it flagged a go-ahead question,
 *   - null when it decided there is none (no buttons, even if the text would
 *     pass asksToProceed),
 *   - undefined when the event carries no decision (older backend) — callers
 *     then fall back to parseQuickPrompt / asksToProceed on the text.
 */
export const proceedPromptFromEvent = (
  evt: ApiProceedPromptEvent | null | undefined,
): AiQuickPrompt | null | undefined => {
  if (typeof evt?.show_proceed_buttons !== 'boolean') return undefined;
  if (!evt.show_proceed_buttons) return null;
  const raw = evt.proceed_prompt;
  const options: QuickReplyOption[] = (raw?.options ?? [])
    .map((o) => ({ label: (o?.label ?? '').trim(), reply: (o?.reply ?? o?.label ?? '').trim() }))
    .filter((o) => o.label && o.reply);
  return {
    text: (raw?.text ?? '').trim(),
    options: options.length ? options : DEFAULT_PROCEED_OPTIONS,
  };
};
